import { PeriodBehavior, RelativeDatePeriod, RelativeUnit } from '../DatePicker';
import { SortDir } from '../hooks';
import { SIDES } from './constants';

export type Filter = {
  expression?: Expression;
  and?: LogicalOperator;
  or?: LogicalOperator;
  not?: Filter;

  /**
   * Apply the filter and all its nested filters to historical data.
   */
  isHistorical?: boolean;
};

export type Criteria = {
  and?: {
    criterias: InnerCriteria[];
  };
  or?: {
    criterias: InnerCriteria[];
  };
};

export type InnerCriteria = {
  in?: {
    lhs: {
      fieldName: string;
    };
    rhs: {
      fieldName: string;
    };
    side: keyof typeof SIDES;
  };
  ipInSubnet?: {
    lhs: {
      fieldName: string;
    };
    rhs: {
      fieldName: string;
    };
    subnetSide: keyof typeof SIDES;
  };
  equals?: Equals;
  any?: { criteria: InnerCriteria; repeatedSide: keyof typeof SIDES };
};

export type Equals = {
  lhs: {
    fieldName: string;
  };
  rhs: {
    fieldName: string;
  };
  caseInsensitive?: boolean;
};

export type RelationsProps = {
  additionalSelectOptions?: any[];
  defaultEmptyFilter?: Filter;
  hideStartAdornment?: boolean;
  enableSide?: boolean;
  enableCaseSensitive?: boolean;
};

export interface LogicalOperator {
  operands: Filter[];
}

export interface BoolCondition {
  equals?: boolean;
  empty?: IsEmpty;
  notEmpty?: IsNotEmpty;
}

export interface IPCondition {
  equals?: string;
  in?: StringGroup;
  notEqual?: string;
  // notIn?: StringGroup;
  gt?: string;
  lt?: string;
  gte?: string;
  lte?: string;
  empty?: IsEmpty;
  notEmpty?: IsNotEmpty;
  ipRange?: string;
  notIpRange?: string;
  cidr?: string;
  notCidr?: string;
}

export interface FixCondition {
  equals?: string;
  in?: StringGroup;
  contains?: string;
  starts?: string;
  ends?: string;
  notEqual?: string;
  notContains?: string;
  not_in?: StringGroup;
  gt?: string;
  gte?: string;
  lt?: string;
  lte?: string;
  empty?: IsEmpty;
  notEmpty?: IsNotEmpty;
}

export interface ArrayCondition {
  underlying: Expression;
  resolution: ArrayResolution;
}

export interface Expression {
  fieldName?: string | null;
  stringCondition?: StringCondition;
  dateCondition?: DateCondition;
  numberCondition?: NumberCondition;
  boolCondition?: BoolCondition;
  ipCondition?: IPCondition;
  fixCondition?: FixCondition;
  arrayCondition?: ArrayCondition;
  fieldCondition?: FieldCondition;
  enumCondition?: EnumCondition;
  caseInsensitive?: boolean;
  populationCondition?: PopulationCondition;
}

export interface NumberGroup {
  from: number;
  to: number;
}

export interface NumberCondition {
  equals?: number;
  between?: NumberGroup;
  gt?: number;
  gte?: number;
  lt?: number;
  lte?: number;
  notEqual?: number;
  empty?: IsEmpty;
  notEmpty?: IsNotEmpty;
}

export interface DateCondition {
  between?: DateGroup;
  relative?: NewRelativeDate;
}

export interface NewRelativeDate {
  value: number;
  unit: string;
  periodBehaviour: PeriodBehavior;
}

export enum RelativeDirection {
  PAST = 'PAST',
  FUTURE = 'FUTURE',
}

export interface DateGroup {
  values: Date[];
}

export interface StringGroup {
  values: string[];
}

interface IsEmpty {}

interface IsNotEmpty {}

export interface TopK {
  k: number;
  dir: SortDir;
}

export interface PopulationCondition {
  name: string;
  topK: TopK;
}

export interface StringCondition {
  equals?: string;
  in?: StringGroup;
  contains?: string;
  starts?: string;
  ends?: string;
  notEqual?: string;
  notContains?: string;
  not_in?: StringGroup;
  empty?: IsEmpty;
  notEmpty?: IsNotEmpty;
  addNullFilter?: boolean;
}

export interface FieldCondition {
  equals?: string;
}

export interface EnumCondition {
  equals?: string;
  notEqual?: string;
  empty?: IsEmpty;
  notEmpty?: IsNotEmpty;
}

export enum OperatorType {
  AND = 'and',
  OR = 'or',
}

export enum ArrayResolution {
  ALL = 'ALL',
  ANY = 'ANY',
  COMPLETE = 'COMPLETE',
}

export enum StringConditionType {
  EQUALS = 'equals',
  CONTAINS = 'contains',
  STARTS = 'starts',
  ENDS = 'ends',
  NOT_EQUAL = 'notEqual',
  EMPTY = 'empty',
  NOT_EMPTY = 'notEmpty',
  NOT_CONTAINS = 'notContains',
}

export enum NumberConditionType {
  EQUALS = 'equals',
  BETWEEN = 'between',
  GT = 'gt',
  GTE = 'gte',
  LT = 'lt',
  LTE = 'lte',
  NOT_EQUAL = 'notEqual',
}
export const convertPeriodPreset = {
  [RelativeUnit.DAYS]: RelativeDatePeriod.days,
  [RelativeUnit.WEEKS]: RelativeDatePeriod.weeks,
  [RelativeUnit.MONTHS]: RelativeDatePeriod.months,
  [RelativeUnit.HOURS]: RelativeDatePeriod.hours,
  [RelativeUnit.MINUTES]: RelativeDatePeriod.minutes,
};
